import LoadError from "../components/LoadError";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../services/api";
import useActionLock from "../hooks/useActionLock";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import ExportData from "../components/ExportData";
import {
  Building,
  CreditCard,
  MapPin,
  Save,
  Settings as SettingsIcon,
  User,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";

import { parametresAPI } from "../services/api";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import RelanceSettings from "../components/RelanceSettings";
import PenaliteSettings from "../components/PenaliteSettings";

const Parametres = () => {
  const tabs = [["entreprise", "Entreprise"], ["facturation", "Facturation"], ["emails", "Emails et relances"], ["securite", "Sécurité"]];
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("entreprise");
  const [saving, setSaving] = useState(false);
  const [settingsBusy, runSettingsSave] = useActionLock();
  // Données locales chargées puis appliquées via react-hook-form

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    watch,
    setValue,
    unregister,
  } = useForm({ shouldFocusError: false });

  useUnsavedChanges(isDirty);

  const loadParametres = useCallback(async () => {
    setLoadError("");
    try {
      const response = await parametresAPI.get();
      reset(response.data);
    } catch (error) {
      setLoadError("Impossible de charger les paramètres. Réessayez avant de les modifier.");
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    loadParametres();
  }, [loadParametres]);

  const onSubmit = (data) => runSettingsSave(async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const response = await parametresAPI.update({ ...data, tva_non_applicable: data.regime_fiscal === "franchise_base" });
      reset(response.data);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la mise à jour des paramètres");
    } finally {
      setSaving(false);
    }
  });

  const [testingSMTP, setTestingSMTP] = useState(false);
  const onTestSMTP = async () => {
    setTestingSMTP(true);
    try {
      const v = getValues();
      const payload = {
        smtp_host: v.smtp_host,
        smtp_port: v.smtp_port || 587,
        smtp_username: v.smtp_username || undefined,
        smtp_password: v.smtp_password || undefined,
        smtp_use_tls: !!v.smtp_use_tls,
        smtp_use_ssl: !!v.smtp_use_ssl,
      };
      const res = await parametresAPI.smtpTest(payload);
      if (res.data?.ok) {
        toast.success("Connexion SMTP réussie");
      } else {
        toast.error(res.data?.message || "Échec du test SMTP");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || "Échec du test SMTP";
      toast.error(msg);
    } finally {
      setTestingSMTP(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (loadError) return <LoadError message={loadError} onRetry={loadParametres} />;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <SettingsIcon className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
          <p className="text-gray-600">
            Votre entreprise, vos valeurs par défaut et vos automatisations
          </p>
        </div>
      </div>

      <div role="tablist" aria-label="Sections des paramètres" className="flex flex-wrap gap-2 rounded-lg bg-gray-100 p-1">
        {tabs.map(([key, label], index) => (
          <button key={key} type="button" id={`tab-${key}`} role="tab" tabIndex={activeTab === key ? 0 : -1} aria-selected={activeTab === key} aria-controls={`settings-${key}`} onClick={() => setActiveTab(key)} onKeyDown={(event) => {
            const nextIndex = event.key === "ArrowRight" ? (index + 1) % tabs.length : event.key === "ArrowLeft" ? (index + tabs.length - 1) % tabs.length : event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : null;
            if (nextIndex === null) return;
            event.preventDefault();
            setActiveTab(tabs[nextIndex][0]);
            document.getElementById(`tab-${tabs[nextIndex][0]}`)?.focus();
          }} className={`rounded-md px-4 py-2 text-sm font-medium ${activeTab === key ? "bg-white text-primary-700 shadow-sm" : "text-gray-600 hover:bg-white"}`}>{label}</button>
        ))}
      </div>
      <div className="flex justify-end"><ExportData /></div>
      <form hidden={activeTab === "securite"} onSubmit={handleSubmit(onSubmit, (invalid) => {
        const field = Object.keys(invalid)[0];
        setActiveTab(["devise", "taux_horaire_defaut", "delai_paiement", "penalites_taux", "penalites_taux_legaux", "banque", "iban", "bic"].includes(field) ? "facturation" : field?.startsWith("smtp") || field?.startsWith("relances") || field === "copie_facture_a" || field === "signature_public_url" ? "emails" : "entreprise");
        if (field === "penalites_taux_legaux") document.querySelectorAll("#settings-facturation details").forEach((details) => { details.open = true; });
        toast.error("Vérifiez les champs indiqués avant de sauvegarder.");
      })} className="space-y-6">
        <fieldset disabled={saving || settingsBusy} className="min-w-0 space-y-6">
        <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <p className="text-sm text-gray-600" role="status">{isDirty ? "Modifications non enregistrées" : "Paramètres enregistrés"}</p>
        {/* Actions */}
        <div className="flex justify-stretch sm:justify-end">
          <button
            type="submit"
            disabled={saving || !isDirty}
            className="btn-primary flex w-full items-center justify-center gap-2 sm:w-auto"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>
              {saving ? "Sauvegarde..." : "Sauvegarder les paramètres"}
            </span>
          </button>
        </div>
        </div>
        <section id="settings-entreprise" role="tabpanel" aria-labelledby="tab-entreprise" hidden={activeTab !== "entreprise"} className="space-y-6">
        {/* Informations personnelles */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="h-5 w-5 mr-2 text-primary-600" />
            Informations personnelles
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="parametres-6922" className="block text-sm font-medium text-gray-700 mb-1">
                Nom complet *
              </label>
              <input aria-invalid={!!errors.nom} aria-describedby={errors.nom ? "error-parametres-nom" : undefined} id="parametres-6922"
                type="text"
                className="input-field"
                {...register("nom", { required: "Le nom est requis" })}
              />
              {errors.nom && (
                <p id="error-parametres-nom" className="text-red-600 text-sm mt-1">
                  {errors.nom.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="parametres-7436" className="block text-sm font-medium text-gray-700 mb-1">
                Société *
              </label>
              <input aria-invalid={!!errors.societe} aria-describedby={errors.societe ? "error-parametres-societe" : undefined} id="parametres-7436"
                type="text"
                className="input-field"
                {...register("societe", { required: "La société est requise" })}
              />
              {errors.societe && (
                <p id="error-parametres-societe" className="text-red-600 text-sm mt-1">
                  {errors.societe.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="parametres-7963" className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input aria-invalid={!!errors.email} aria-describedby={errors.email ? "error-parametres-email" : undefined} id="parametres-7963"
                type="email"
                className="input-field"
                {...register("email", {
                  required: "L'email est requis",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email invalide",
                  },
                })}
              />
              {errors.email && (
                <p id="error-parametres-email" className="text-red-600 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="parametres-8682" className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone *
              </label>
              <input aria-invalid={!!errors.telephone} aria-describedby={errors.telephone ? "error-parametres-telephone" : undefined} id="parametres-8682"
                type="tel"
                className="input-field"
                {...register("telephone", {
                  required: "Le téléphone est requis",
                })}
              />
              {errors.telephone && (
                <p id="error-parametres-telephone" className="text-red-600 text-sm mt-1">
                  {errors.telephone.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Adresse */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-primary-600" />
            Adresse
          </h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="parametres-9565" className="block text-sm font-medium text-gray-700 mb-1">
                Adresse *
              </label>
              <input aria-invalid={!!errors.adresse} aria-describedby={errors.adresse ? "error-parametres-adresse" : undefined} id="parametres-9565"
                type="text"
                className="input-field"
                {...register("adresse", { required: "L'adresse est requise" })}
              />
              {errors.adresse && (
                <p id="error-parametres-adresse" className="text-red-600 text-sm mt-1">
                  {errors.adresse.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="parametres-10163" className="block text-sm font-medium text-gray-700 mb-1">
                  Ville *
                </label>
                <input aria-invalid={!!errors.ville} aria-describedby={errors.ville ? "error-parametres-ville" : undefined} id="parametres-10163"
                  type="text"
                  className="input-field"
                  {...register("ville", { required: "La ville est requise" })}
                />
                {errors.ville && (
                  <p id="error-parametres-ville" className="text-red-600 text-sm mt-1">
                    {errors.ville.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="parametres-10710" className="block text-sm font-medium text-gray-700 mb-1">
                  Code postal *
                </label>
                <input aria-invalid={!!errors.code_postal} aria-describedby={errors.code_postal ? "error-parametres-code_postal" : undefined} id="parametres-10710"
                  type="text"
                  className="input-field"
                  {...register("code_postal", {
                    required: "Le code postal est requis",
                  })}
                />
                {errors.code_postal && (
                  <p id="error-parametres-code_postal" className="text-red-600 text-sm mt-1">
                    {errors.code_postal.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="parametres-11325" className="block text-sm font-medium text-gray-700 mb-1">
                  Pays *
                </label>
                <select aria-invalid={!!errors.pays} aria-describedby={errors.pays ? "error-parametres-pays" : undefined} id="parametres-11325" className="input-field" {...register("pays")}>
                  <option value="France">France</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Entreprise émettrice établie en France. Les pays des clients se choisissent dans leurs fiches.</p>
                {errors.pays && (
                  <p id="error-parametres-pays" className="text-red-600 text-sm mt-1">
                    {errors.pays.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Informations fiscales */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building className="h-5 w-5 mr-2 text-primary-600" />
            Informations fiscales
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="parametres-12373" className="block text-sm font-medium text-gray-700 mb-1">SIRET *</label>
              <input aria-invalid={!!errors.siret} aria-describedby={errors.siret ? "error-parametres-siret" : undefined} id="parametres-12373" className="input-field" {...register("siret", { required: "Le SIRET est requis" })} />
              {errors.siret && <p id="error-parametres-siret" className="text-red-600 text-sm">{errors.siret.message}</p>}
            </div>
            <div>
              <label htmlFor="parametres-12707" className="block text-sm font-medium text-gray-700 mb-1">Régime fiscal de l'entreprise</label>
              <select id="parametres-12707" className="input-field" {...register("regime_fiscal")}>
                <option value="franchise_base">Franchise en base de TVA</option>
                <option value="a_valider">Autre régime, à valider avant émission</option>
              </select>
            </div>
          </div>
          <div className="mt-4 rounded-lg bg-gray-50 p-4 text-sm text-gray-700" aria-live="polite">
            {watch("regime_fiscal") === "franchise_base" ? <>
              <p className="font-medium">Mentions générées automatiquement</p>
              <p>France : TVA non applicable, art. 293 B du CGI.</p>
              <p>Canada, professionnel (règle générale) : TVA non applicable en France, art. 259-1 du CGI.</p>
              <p className="mt-2 text-xs text-gray-500">La nature du client et son régime validé déterminent la mention. Les cas particuliers se règlent dans la fiche client.</p>
            </> : <p>Ce régime n'est pas pris en charge. L'émission de nouveaux documents sera bloquée jusqu'à validation d'un régime compatible.</p>}
          </div>
        </div>

        </section>
        <section id="settings-facturation" role="tabpanel" aria-labelledby="tab-facturation" hidden={activeTab !== "facturation"} className="space-y-6">
          <p className="text-sm text-gray-600">Chaque client conserve son tarif et son délai. Ces valeurs préparent les nouveaux clients et ne changent pas les factures existantes.</p>
        {/* Paramètres de facturation */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Valeurs par défaut pour les nouveaux clients
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="parametres-14572" className="block text-sm font-medium text-gray-700 mb-1">
                Devise proposée *
              </label>
              <select aria-invalid={!!errors.devise} aria-describedby={errors.devise ? "error-parametres-devise" : undefined} id="parametres-14572"
                className="input-field"
                {...register("devise", { required: "La devise est requise" })}
              >
                <option value="EUR">EUR</option>
                <option value="CAD">CAD (Dollar canadien)</option>
              </select>
              {errors.devise && (
                <p id="error-parametres-devise" className="text-red-600 text-sm mt-1">
                  {errors.devise.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="parametres-15215" className="block text-sm font-medium text-gray-700 mb-1">
                Tarif horaire ({watch("devise")} / heure) *
              </label>
              <input aria-invalid={!!errors.taux_horaire_defaut} aria-describedby={errors.taux_horaire_defaut ? "error-parametres-taux_horaire_defaut" : undefined} id="parametres-15215"
                type="number"
                step="0.01"
                min="0"
                className="input-field"
                {...register("taux_horaire_defaut", {
                  valueAsNumber: true,
                  required: "Le taux horaire est requis",
                  min: { value: 0, message: "Le taux doit être positif" },
                })}
              />
              {errors.taux_horaire_defaut && (
                <p id="error-parametres-taux_horaire_defaut" className="text-red-600 text-sm mt-1">
                  {errors.taux_horaire_defaut.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="parametres-16019" className="block text-sm font-medium text-gray-700 mb-1">
                Délai de paiement proposé (jours) *
              </label>
              <input aria-invalid={!!errors.delai_paiement} aria-describedby={errors.delai_paiement ? "error-parametres-delai_paiement" : undefined} id="parametres-16019"
                type="number"
                min="1"
                className="input-field"
                {...register("delai_paiement", {
                  valueAsNumber: true,
                  max: { value: 365, message: "Maximum 365 jours" },
                  validate: (v) => Number.isInteger(v) || "Choisissez un nombre de jours entier",
                  required: "Le délai de paiement est requis",
                  min: {
                    value: 1,
                    message: "Le délai doit être d'au moins 1 jour",
                  },
                })}
              />
              {errors.delai_paiement && (
                <p id="error-parametres-delai_paiement" className="text-red-600 text-sm mt-1">
                  {errors.delai_paiement.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="parametres-17014" className="block text-sm font-medium text-gray-700 mb-1">
                Pénalités de retard (multiplicateur) *
              </label>
              <input aria-invalid={!!errors.penalites_taux} aria-describedby={errors.penalites_taux ? "error-parametres-penalites_taux" : undefined} id="parametres-17014"
                type="number"
                step="0.1"
                min="3"
                className="input-field"
                {...register("penalites_taux", {
                  valueAsNumber: true,
                  required: "Le taux de pénalités est requis",
                  min: { value: 3, message: "Minimum 3 pour le régime français entre professionnels" },
                })}
              />
              {errors.penalites_taux && (
                <p id="error-parametres-penalites_taux" className="text-red-600 text-sm mt-1">
                  {errors.penalites_taux.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Informations bancaires */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CreditCard className="h-5 w-5 mr-2 text-primary-600" />
            Informations bancaires
          </h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="parametres-18178" className="block text-sm font-medium text-gray-700 mb-1">
                Nom de la banque *
              </label>
              <input aria-invalid={!!errors.banque} aria-describedby={errors.banque ? "error-parametres-banque" : undefined} id="parametres-18178"
                type="text"
                className="input-field"
                {...register("banque", {
                  required: "Le nom de la banque est requis",
                })}
              />
              {errors.banque && (
                <p id="error-parametres-banque" className="text-red-600 text-sm mt-1">
                  {errors.banque.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="parametres-18754" className="block text-sm font-medium text-gray-700 mb-1">
                Adresse de la banque
              </label>
              <input id="parametres-18754"
                type="text"
                className="input-field"
                placeholder="(Optionnel) Adresse, code postal, ville, pays"
                {...register("banque_adresse")}
              />
            </div>

            <div>
              <label htmlFor="parametres-19161" className="block text-sm font-medium text-gray-700 mb-1">
                IBAN *
              </label>
              <input aria-invalid={!!errors.iban} aria-describedby={errors.iban ? "error-parametres-iban" : undefined} id="parametres-19161"
                type="text"
                className="input-field font-mono"
                {...register("iban", { required: "L'IBAN est requis" })}
              />
              {errors.iban && (
                <p id="error-parametres-iban" className="text-red-600 text-sm mt-1">
                  {errors.iban.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="parametres-19681" className="block text-sm font-medium text-gray-700 mb-1">
                BIC *
              </label>
              <input aria-invalid={!!errors.bic} aria-describedby={errors.bic ? "error-parametres-bic" : undefined} id="parametres-19681"
                type="text"
                className="input-field font-mono"
                {...register("bic", { required: "Le BIC est requis" })}
              />
              {errors.bic && (
                <p id="error-parametres-bic" className="text-red-600 text-sm mt-1">
                  {errors.bic.message}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="parametres-20196" className="block text-sm font-medium text-gray-700 mb-1">
                BIC intermédiaire
              </label>
              <input id="parametres-20196"
                type="text"
                className="input-field font-mono"
                {...register("bic_intermediaire")}
              />
            </div>
          </div>
        </div>

          <details className="card"><summary className="cursor-pointer font-medium">Taux légaux avancés</summary><div className="mt-4">
        <PenaliteSettings register={register} watch={watch} setValue={setValue} unregister={unregister} errors={errors} />
          </div></details>
        </section>
        <section id="settings-emails" role="tabpanel" aria-labelledby="tab-emails" hidden={activeTab !== "emails"} className="space-y-6">
        <div className="card">
          <h2 className="mb-3 text-lg font-semibold">Signature des devis</h2>
          <label htmlFor="signature-public-url" className="block text-sm font-medium">URL publique de FacturaLite</label>
          <input id="signature-public-url" className="input-field mt-1" type="url" placeholder="https://facturation.exemple.fr" {...register("signature_public_url")} />
          <p className="mt-2 text-sm text-gray-600">L'adresse accessible à vos clients, en HTTPS, sans chemin. Elle sert aux liens de signature. Les codes utilisent votre serveur SMTP. Pour un essai local : http://localhost:3000.</p>
        </div>
        {/* E-mail et envoi automatique */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            E-mail et envoi automatique
          </h2>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register("email_auto_envoi")}
              />
              <span className="ml-2 text-sm text-gray-700">
                Envoyer automatiquement la facture par e-mail lors de la
                création
              </span>
            </label>

            <div>
              <label htmlFor="parametres-21713" className="block text-sm font-medium text-gray-700 mb-1">
                Copie des factures et devis à (CCI)
              </label>
              <input aria-invalid={!!errors.copie_facture_a} aria-describedby={errors.copie_facture_a ? "error-parametres-copie_facture_a" : undefined} id="parametres-21713"
                type="email"
                className="input-field"
                placeholder="email@example.com"
                {...register("copie_facture_a", {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email invalide",
                  },
                })}
              />
              {errors.copie_facture_a && (
                <p id="error-parametres-copie_facture_a" className="text-red-600 text-sm mt-1">
                  {errors.copie_facture_a.message}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                Cet email recevra une copie de toutes les factures et de tous les devis envoyés
              </p>
            </div>

            <details className="rounded-lg border border-gray-200 p-4">
              <summary className="cursor-pointer font-medium">Configuration SMTP avancée</summary>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="parametres-22908" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Host
                </label>
                <input id="parametres-22908"
                  type="text"
                  className="input-field"
                  {...register("smtp_host")}
                />
              </div>
              <div>
                <label htmlFor="parametres-23242" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Port
                </label>
                <input id="parametres-23242"
                  type="number"
                  className="input-field"
                  {...register("smtp_port", { valueAsNumber: true })}
                />
              </div>
              <div>
                <label htmlFor="parametres-23603" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Username
                </label>
                <input id="parametres-23603"
                  type="text"
                  className="input-field"
                  {...register("smtp_username")}
                />
              </div>
              <div>
                <label htmlFor="parametres-23945" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Password
                </label>
                <input id="parametres-23945"
                  type="password"
                  className="input-field"
                  {...register("smtp_password")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sécurité
                </label>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      {...register("smtp_use_tls")}
                    />
                    <span className="text-sm text-gray-700">STARTTLS</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      {...register("smtp_use_ssl")}
                    />
                    <span className="text-sm text-gray-700">SSL/TLS</span>
                  </label>
                </div>
              </div>
              <div className="md:col-span-2">
                <button
                  type="button"
                  onClick={onTestSMTP}
                  disabled={testingSMTP}
                  className="btn-secondary"
                >
                  {testingSMTP
                    ? "Test en cours..."
                    : "Tester la configuration SMTP"}
                </button>
              </div>
            </div>
            </details>
          </div>
        </div>

        <RelanceSettings register={register} watch={watch} getValues={getValues} errors={errors} />

        </section>
        </fieldset>
      </form>

      {/* Sécurité et authentification - EN DEHORS du formulaire principal */}
      <div id="settings-securite" role="tabpanel" aria-labelledby="tab-securite" hidden={activeTab !== "securite"} className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-3 mb-6">
          <User className="h-6 w-6 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900">Sécurité</h2>
        </div>

        <ChangePasswordSection />
      </div>
    </div>
  );
};

// Composant pour changer le mot de passe
export const ChangePasswordSection = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordBusy, runPasswordChange] = useActionLock();

  const allowPasswordNavigation = useUnsavedChanges(!!(currentPassword || newPassword || confirmPassword));

  const handleChangePassword = async (e) => {
    e.preventDefault();

    return runPasswordChange(async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    if (newPassword.length < 15) {
      toast.error("Le mot de passe doit contenir au moins 15 caractères");
      return;
    }

    setLoading(true);
    try {
      const { data } = await authAPI.changePassword(currentPassword, newPassword);
      sessionStorage.setItem("token", data.access_token);
      sessionStorage.setItem("username", data.username);

      toast.success("Mot de passe modifié avec succès");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      allowPasswordNavigation();
      navigate("/", { replace: true });
    } catch (error) {
      toast.error(error.message || "Erreur lors du changement de mot de passe");
    } finally {
      setLoading(false);
    }
    });
  };

  return (
    <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
      <p className="text-sm text-gray-600 mb-4">
        Les autres sessions seront déconnectées après le changement. Changez votre mot de passe pour sécuriser votre accès à l'application.
      </p>

      <div>
        <label htmlFor="parametres-28301" className="block text-sm font-medium text-gray-700 mb-1">
          Mot de passe actuel
        </label>
        <input id="parametres-28301"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
          className="input-field"
          placeholder="Votre mot de passe actuel"
        />
      </div>

      <div>
        <label htmlFor="parametres-28741" className="block text-sm font-medium text-gray-700 mb-1">
          Nouveau mot de passe
        </label>
        <input id="parametres-28741"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={15}
          className="input-field"
          placeholder="Minimum 15 caractères"
        />
      </div>

      <div>
        <label htmlFor="parametres-29191" className="block text-sm font-medium text-gray-700 mb-1">
          Confirmer le nouveau mot de passe
        </label>
        <input id="parametres-29191"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={15}
          className="input-field"
          placeholder="Retapez le nouveau mot de passe"
        />
      </div>

      <button
        type="submit"
        disabled={loading || passwordBusy || !currentPassword || newPassword.length < 15 || newPassword !== confirmPassword}
        className="btn-secondary flex w-full items-center justify-center gap-2 sm:w-auto"
      >
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        ) : (
          <Save className="h-4 w-4" />
        )}
        <span>{loading ? "Modification..." : "Changer le mot de passe"}</span>
      </button>
    </form>
  );
};

export default Parametres;
